import { useState, useEffect } from 'react';
import { API, useAuth } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  Shield, Users, DollarSign, TrendingUp, Award, Crown,
  AlertTriangle, CheckCircle2, Clock, Target, BarChart3,
  FileText, Send, RefreshCw, ChevronDown, ChevronUp,
  UserPlus, Percent, Calendar, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const SuperSoldierDashboard = ({ superSoldierData }) => {
  const { token, user } = useAuth();
  const [platoon, setPlatoon] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSoldier, setExpandedSoldier] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [overrideData, setOverrideData] = useState(null);
  const [reportData, setReportData] = useState({
    feature_requests: '',
    competitor_intel: '',
    retention_notes: '',
    action_items: ''
  });
  const [submittingReport, setSubmittingReport] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [token]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch Super Soldier dashboard data
      const dashboardResponse = await axios.get(`${API}/admin/team/super-soldier/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(dashboardResponse.data);
      
      // If we have a super soldier ID, fetch platoon and override data
      const superSoldierId = dashboardResponse.data?.super_soldier_id || superSoldierData?.super_soldier_id;
      if (superSoldierId) {
        // Fetch platoon
        const platoonResponse = await axios.get(`${API}/admin/team/platoon/${superSoldierId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPlatoon(platoonResponse.data.soldiers || []);
        
        // Fetch override earnings
        const overrideResponse = await axios.get(`${API}/admin/team/super-soldier/${superSoldierId}/override`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOverrideData(overrideResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // If API fails, use the passed superSoldierData if available
      if (superSoldierData) {
        setDashboardData(superSoldierData);
      }
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReport = async () => {
    setSubmittingReport(true);
    try {
      await axios.post(`${API}/admin/team/super-soldier/notes`, {
        soldier_id: dashboardData?.super_soldier_id || 'platoon_health_report',
        note_type: 'health_report',
        content: JSON.stringify(reportData)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Platoon Health Report submitted to CEO');
      setShowReportModal(false);
      setReportData({ feature_requests: '', competitor_intel: '', retention_notes: '', action_items: '' });
    } catch (error) {
      console.error('Failed to submit report:', error);
      toast.error('Failed to submit report');
    } finally {
      setSubmittingReport(false);
    }
  };

  // Calculate aggregated stats from real data
  const totalSeats = platoon.reduce((sum, s) => sum + (s.active_seats || 0), 0);
  const avgRetention = platoon.length > 0 
    ? (platoon.reduce((sum, s) => sum + (s.month2_retention || 0), 0) / platoon.length).toFixed(1)
    : 0;
  const totalPendingBonus = platoon.reduce((sum, s) => sum + (s.pending_bonus || 0), 0);
  const totalLifetimeEarnings = platoon.reduce((sum, s) => sum + (s.lifetime_earnings || 0), 0);
  const soldiersNeedingIntervention = platoon.filter(s => (s.month2_retention || 0) < 85).length;

  // Use real override data if available, otherwise calculate projection
  const monthlyOverride = overrideData?.total_override_earnings || (() => {
    const proSeats = Math.floor(totalSeats * 0.6); // Assume 60% Pro
    const eliteSeats = totalSeats - proSeats;
    return (proSeats * 0.90) + (eliteSeats * 1.50);
  })();

  return (
    <div className="space-y-6" data-testid="super-soldier-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Super Soldier Command Center</h2>
              <p className="text-zinc-400">Power 2 Executive Dashboard</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchDashboardData}
            className="border-zinc-700 text-zinc-300"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => setShowReportModal(true)}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
          >
            <FileText className="w-4 h-4 mr-2" />
            Submit Health Report
          </Button>
        </div>
      </div>

      {/* Executive Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-5 h-5 text-purple-400" />
              <Badge variant="outline" className="text-purple-400 border-purple-500/30">
                {platoon.length}/25
              </Badge>
            </div>
            <div className="text-2xl font-bold text-white">{platoon.length}</div>
            <div className="text-sm text-zinc-400">Platoon Soldiers</div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Target className="w-5 h-5 text-green-400" />
              <Badge variant="outline" className={`${avgRetention >= 85 ? 'text-green-400 border-green-500/30' : 'text-red-400 border-red-500/30'}`}>
                {avgRetention >= 85 ? 'Healthy' : 'At Risk'}
              </Badge>
            </div>
            <div className="text-2xl font-bold text-white">{avgRetention}%</div>
            <div className="text-sm text-zinc-400">Avg Month 2 Retention</div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <BarChart3 className="w-5 h-5 text-amber-400" />
              <span className="text-xs text-zinc-500">Active</span>
            </div>
            <div className="text-2xl font-bold text-white">{totalSeats.toLocaleString()}</div>
            <div className="text-sm text-zinc-400">Total Stylist Seats</div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-5 h-5 text-green-400" />
              <span className="text-xs text-green-400">3% Override</span>
            </div>
            <div className="text-2xl font-bold text-white">${monthlyOverride.toFixed(2)}</div>
            <div className="text-sm text-zinc-400">Monthly Management Override</div>
          </CardContent>
        </Card>
      </div>

      {/* Alert Banner */}
      {soldiersNeedingIntervention > 0 && (
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-red-400">Intervention Required</h4>
              <p className="text-sm text-zinc-300">
                {soldiersNeedingIntervention} soldier(s) have Month 2 retention below 85%. 
                The 60-Day Rule requires immediate action to maintain your Super Soldier status.
              </p>
            </div>
            <Button 
              variant="outline" 
              className="ml-auto border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              View Details
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Platoon Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-400" />
            Platoon Roster
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Your 25-Soldier team and their performance metrics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left py-3 px-4 text-zinc-400 font-medium">Soldier</th>
                  <th className="text-center py-3 px-4 text-zinc-400 font-medium">Status</th>
                  <th className="text-center py-3 px-4 text-zinc-400 font-medium">M1 Ret.</th>
                  <th className="text-center py-3 px-4 text-zinc-400 font-medium">M2 Ret.</th>
                  <th className="text-center py-3 px-4 text-zinc-400 font-medium">Active Seats</th>
                  <th className="text-right py-3 px-4 text-zinc-400 font-medium">Lifetime Earnings</th>
                  <th className="text-center py-3 px-4 text-zinc-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-zinc-500">
                      Loading platoon data...
                    </td>
                  </tr>
                ) : platoon.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8">
                      <div className="space-y-3">
                        <Users className="w-12 h-12 mx-auto text-zinc-600" />
                        <p className="text-zinc-400">No soldiers in your platoon yet</p>
                        <p className="text-zinc-500 text-sm max-w-md mx-auto">
                          As a Super Soldier, you can manage up to 25 soldiers in your platoon. 
                          Contact the CEO to get soldiers assigned to your platoon.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : platoon.map((soldier) => (
                  <tr key={soldier.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-medium text-white">{soldier.name}</div>
                        <div className="text-xs text-zinc-500">{soldier.salon}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Badge 
                        variant="outline" 
                        className={soldier.month2_retention >= 85 
                          ? 'text-green-400 border-green-500/30' 
                          : 'text-amber-400 border-amber-500/30'
                        }
                      >
                        {soldier.month2_retention >= 85 ? 'Active' : 'Warning'}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-center text-zinc-300">{soldier.month1_retention}%</td>
                    <td className="py-3 px-4 text-center">
                      <span className={soldier.month2_retention >= 85 ? 'text-green-400' : 'text-red-400'}>
                        {soldier.month2_retention}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center text-zinc-300">{soldier.active_seats}</td>
                    <td className="py-3 px-4 text-right text-green-400 font-medium">
                      ${soldier.lifetime_earnings.toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedSoldier(expandedSoldier === soldier.id ? null : soldier.id)}
                        className="text-zinc-400 hover:text-white"
                      >
                        {expandedSoldier === soldier.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Earnings Projection Calculator */}
      <Card className="bg-gradient-to-r from-purple-500/10 to-pink-500/5 border-purple-500/30">
        <CardHeader>
          <CardTitle className="text-purple-400 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            3-Year Management Override Projection
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-4 rounded-lg bg-zinc-950/50">
              <div className="text-sm text-zinc-500 mb-1">Year 1 (12,500 seats)</div>
              <div className="text-3xl font-bold text-blue-400">$11,250 - $18,750</div>
              <div className="text-xs text-zinc-500">/month in residuals</div>
            </div>
            <div className="text-center p-4 rounded-lg bg-zinc-950/50">
              <div className="text-sm text-zinc-500 mb-1">Year 2 (25,000 seats)</div>
              <div className="text-3xl font-bold text-purple-400">$22,500 - $37,500</div>
              <div className="text-xs text-zinc-500">/month in residuals</div>
            </div>
            <div className="text-center p-4 rounded-lg bg-zinc-950/50">
              <div className="text-sm text-zinc-500 mb-1">Year 3 (37,500 seats)</div>
              <div className="text-3xl font-bold text-amber-400">$33,750 - $56,250</div>
              <div className="text-xs text-zinc-500">/month in residuals</div>
            </div>
          </div>
          <p className="text-xs text-zinc-500 text-center mt-4">
            *Based on 1,500 seats per Soldier × 25 Soldiers = 37,500 total seats at full scale
          </p>
        </CardContent>
      </Card>

      {/* Health Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-zinc-900 border-zinc-700">
            <CardHeader className="border-b border-zinc-800">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-400" />
                    Monthly Platoon Health Report
                  </CardTitle>
                  <CardDescription className="text-zinc-400">
                    Submit strategic insights to CEO (George)
                  </CardDescription>
                </div>
                <button 
                  onClick={() => setShowReportModal(false)}
                  className="text-zinc-400 hover:text-white p-2"
                >
                  ✕
                </button>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Feature Requests from Soldiers
                </label>
                <Textarea
                  value={reportData.feature_requests}
                  onChange={(e) => setReportData(prev => ({ ...prev, feature_requests: e.target.value }))}
                  placeholder="List any feature requests or pain points reported by your platoon..."
                  className="bg-zinc-800 border-zinc-700 text-white"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Competitor Intelligence
                </label>
                <Textarea
                  value={reportData.competitor_intel}
                  onChange={(e) => setReportData(prev => ({ ...prev, competitor_intel: e.target.value }))}
                  placeholder="Any competitor movements or market insights..."
                  className="bg-zinc-800 border-zinc-700 text-white"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Retention Notes
                </label>
                <Textarea
                  value={reportData.retention_notes}
                  onChange={(e) => setReportData(prev => ({ ...prev, retention_notes: e.target.value }))}
                  placeholder="Notes on soldiers with retention issues and remedial actions taken..."
                  className="bg-zinc-800 border-zinc-700 text-white"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Action Items for Next Month
                </label>
                <Textarea
                  value={reportData.action_items}
                  onChange={(e) => setReportData(prev => ({ ...prev, action_items: e.target.value }))}
                  placeholder="List your planned actions for the coming month..."
                  className="bg-zinc-800 border-zinc-700 text-white"
                  rows={3}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 border-zinc-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitReport}
                  disabled={submittingReport}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                >
                  {submittingReport ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Report
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SuperSoldierDashboard;
